// src/pages/Home.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth } from '../context/AuthContext';
import Listing from '../components/Listing';
import FiltroHome from '../components/FiltroHome';
import IconeComparador from '../components/IconeComparador';

import {
  getAlgoliaClient,
  getGroups,
  searchProperties,
} from '../services/searchClient';

const INDEX_NAME = import.meta.env.VITE_ALGOLIA_INDEX?.trim() || 'imoveis';

const DEBUG_ALGOLIA =
  import.meta.env.DEV || localStorage.getItem('debugAlgolia') === '1';

// atributos que o Card usa
const ATTRS_TO_RETRIEVE = [
  'objectID',
  'refCodigo',
  'referencia',
  'titulo',
  'cidade',
  'bairro',
  'tipo',
  'preco',
  'dormitorios',
  'banheiros',
  'vagas',
  'thumbnail',
  'imagens',
  'ownerId',
  'ownerNome',
  'statusNegocio',
  'temPlaca',

  // selos / permuta
  'aceleradorVendas',
  'badges',
  'permuta',
  'permutaResumo',
  'aceitaPermuta',
  'aceitaPermutaVeiculos',
  'aceitaPermutaImoveis',
];

const toSlug = (s = '') =>
  String(s)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

function resolveScope(f) {
  return {
    meus: f?.meus ?? f?.origemMeus ?? true,
    socios: f?.socios ?? f?.origemSocios ?? true,
    parceiros: f?.parceiros ?? f?.origemParceiros ?? true,
  };
}

/**
 * Constrói o facet de donos de forma segura:
 * - Inclui sempre meUid quando "Meus" estiver marcado
 * - Ignora arrays vazios
 * - Retorna null quando não há nada a restringir
 */
function buildOwnerFacet(filtros, groups, meUid) {
  const scope = resolveScope(filtros);
  const owners = new Set();

  if (scope.meus && meUid) owners.add(meUid);
  if (scope.socios && Array.isArray(groups?.socios)) {
    groups.socios.forEach((id) => id && owners.add(id));
  }
  if (scope.parceiros && Array.isArray(groups?.parceiros)) {
    groups.parceiros.forEach((id) => id && owners.add(id));
  }

  return owners.size ? [...owners].map((id) => `ownerId:${id}`) : null;
}

/**
 * ✅ Normaliza o hit do Algolia pro formato do app:
 * - id
 * - thumb e imagens (sempre array)
 * - garante referencia/refCodigo
 * - injeta origem (meu/socio/parceiro) quando possível
 */
function mapHitToItem(hit, meUid, groups) {
  const imgs = Array.isArray(hit?.imagens) ? hit.imagens : [];
  const thumb =
    hit?.thumbnail ||
    (typeof imgs[0] === 'string' ? imgs[0] : imgs[0]?.url) ||
    '';

  const ownerId =
    hit?.ownerId ||
    hit?.usuarioId ||
    hit?.criadoPorUid ||
    hit?.usuario?.uid ||
    null;

  let origem = hit?.origem;
  if (!origem && ownerId && meUid) {
    if (ownerId === meUid) origem = 'meu';
    else if (Array.isArray(groups?.socios) && groups.socios.includes(ownerId))
      origem = 'socio';
    else if (
      Array.isArray(groups?.parceiros) &&
      groups.parceiros.includes(ownerId)
    )
      origem = 'parceiro';
  }

  return {
    id: hit.objectID,
    objectID: hit.objectID,
    thumbnail: thumb,
    imagens: imgs,
    referencia: hit.referencia || hit.refCodigo || '',
    refCodigo: hit.refCodigo || hit.referencia || '',
    origem: origem || 'parceiro',
    ...hit,
  };
}

export default function Home() {
  const { usuario } = useAuth();

  const [imoveis, setImoveis] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [loading, setLoading] = useState(true);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [ultimoDoc, setUltimoDoc] = useState(0); // página Algolia
  const [temMais, setTemMais] = useState(true);

  const [groups, setGroups] = useState({ me: null, socios: [], parceiros: [] });
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  const jaCarregouRef = useRef(false);
  const temMaisRef = useRef(temMais);
  const ultimoDocRef = useRef(ultimoDoc);

  useEffect(() => {
    temMaisRef.current = temMais;
  }, [temMais]);

  useEffect(() => {
    ultimoDocRef.current = ultimoDoc;
  }, [ultimoDoc]);

  // ✅ defaults de escopo (pra não “sumir” tudo se filtros vier vazio)
  const scope = useMemo(() => resolveScope(filtros), [filtros]);

  // Carrega cliente Algolia e grupos
  useEffect(() => {
    (async () => {
      if (!usuario?.uid) return;
      try {
        await getAlgoliaClient({ indexName: INDEX_NAME });
        const g = (await getGroups()) || {};
        setGroups({
          me: g.me || usuario.uid,
          socios: Array.isArray(g.socios) ? g.socios : [],
          parceiros: Array.isArray(g.parceiros) ? g.parceiros : [],
        });
        setGroupsLoaded(true);
      } catch (e) {
        console.error('Algolia groups error:', e);
        setGroups({ me: usuario.uid, socios: [], parceiros: [] });
        setGroupsLoaded(true);
      }
    })();
  }, [usuario?.uid]);

  const buildAlgoliaParams = useCallback(
    (page = 0) => {
      const q = filtros.q ?? filtros.termo ?? filtros.codigo ?? '';

      // atuação: só filtra se houver valor válido
      const estado = (
        filtros.estado ||
        usuario?.estado ||
        usuario?.uf ||
        ''
      ).trim();
      const cidade = (filtros.cidade || usuario?.cidade || '').trim();
      const bairro = (filtros.bairro || '').trim();

      const tipo = filtros.tipo ?? '';
      const precoMin = filtros.precoMin ?? filtros.minPreco ?? '';
      const precoMax = filtros.precoMax ?? filtros.maxPreco ?? '';
      const quartosExatos = filtros.quartosExatos ?? '';
      const minQuartos = filtros.minQuartos ?? '';
      const piscina = !!filtros.piscina;
      const churrasqueira = !!filtros.churrasqueira;
      const condominio = !!filtros.condominio;
      const financiamento = !!filtros.financiamento;

      const facetFilters = [];
      const numericFilters = [];

      // ✅ publicado sempre
      // (no seu index, isso pode ser "publicado:true" OU "status:publicado".
      // Mantive o seu padrão, mas também adiciono fallback via OR.)
      facetFilters.push(['publicado:true', 'status:publicado']);

      // ✅ donos (seguro)
      const ownerOr = buildOwnerFacet(filtros, groups, usuario?.uid);
      if (ownerOr) facetFilters.push(ownerOr);

      // atuação
      if (estado && toSlug(estado)) {
        // se seu index usa "estado_slug", ok
        facetFilters.push([`estado_slug:${toSlug(estado)}`]);
      }
      if (cidade) {
        const c = cidade;
        const cidadeOR = [`cidade:${c}`, `cidade_slug:${toSlug(c)}`];
        if (estado)
          cidadeOR.push(`cidade_slug_full:${toSlug(`${estado}-${c}`)}`);
        facetFilters.push(cidadeOR);
      }
      if (bairro) {
        const b = bairro;
        const bairroOR = [`bairro:${b}`, `bairro_slug:${toSlug(b)}`];
        if (estado && cidade)
          bairroOR.push(
            `bairro_slug_full:${toSlug(`${estado}-${cidade}-${b}`)}`
          );
        facetFilters.push(bairroOR);
      }

      // outros facets
      if (tipo) facetFilters.push([`tipo:${tipo}`]);
      if (piscina) facetFilters.push(['piscina:true']);
      if (churrasqueira) facetFilters.push(['churrasqueira:true']);
      if (condominio) facetFilters.push(['condominio:true']);
      if (financiamento) facetFilters.push(['financiamento:true']);

      // numeric
      if (precoMin) numericFilters.push(`preco>=${Number(precoMin)}`);
      if (precoMax) numericFilters.push(`preco<=${Number(precoMax)}`);

      if (quartosExatos) {
        numericFilters.push(`dormitorios=${Number(quartosExatos)}`);
      } else if (minQuartos) {
        numericFilters.push(`dormitorios>=${Number(minQuartos)}`);
      }

      const params = {
        query: q,
        hitsPerPage: 12,
        page,
        facetFilters: facetFilters.length ? facetFilters : undefined,
        numericFilters: numericFilters.length ? numericFilters : undefined,
        attributesToRetrieve: ATTRS_TO_RETRIEVE,
        attributesToHighlight: [],
      };

      if (DEBUG_ALGOLIA) {
        console.log('[ALGOLIA] index:', INDEX_NAME);
        console.log('[ALGOLIA] params', JSON.stringify(params, null, 2));
      }

      return params;
    },
    [
      filtros,
      groups,
      usuario?.uid,
      usuario?.estado,
      usuario?.uf,
      usuario?.cidade,
    ]
  );

  const fetchImoveis = useCallback(
    async (isLoadMore = false) => {
      if (!usuario?.uid || !groupsLoaded) {
        setImoveis([]);
        setTemMais(false);
        setLoading(false);
        setCarregandoMais(false);
        return;
      }
      if (isLoadMore && !temMaisRef.current) return;

      setLoading(!isLoadMore);
      setCarregandoMais(isLoadMore);

      try {
        const { type, index } = await getAlgoliaClient({
          indexName: INDEX_NAME,
        });

        // fallback Firestore (quando Algolia indisponível)
        if (type !== 'algolia') {
          const { items } = await searchProperties({ hitsPerPage: 12 });
          setImoveis(items || []);
          setUltimoDoc(0);
          setTemMais(false);
          return;
        }

        const nextPage = isLoadMore ? (ultimoDocRef.current || 0) + 1 : 0;
        const params = buildAlgoliaParams(nextPage);
        const res = await index.search(params.query, params);

        if (DEBUG_ALGOLIA) {
          console.log('[ALGOLIA] result', {
            page: res.page,
            hitsOnPage: res.hits?.length ?? 0,
            nbHits: res.nbHits,
            nbPages: res.nbPages,
            exhaustive: res.exhaustiveNbHits,
          });
          if ((res.hits?.length ?? 0) === 0) {
            console.warn(
              '[ALGOLIA] zero hits → verifique facets/numericFilters acima.'
            );
          }
        }

        const novos = (res.hits || []).map((h) =>
          mapHitToItem(h, usuario.uid, groups)
        );

        // ✅ aplica origem local (caso o user tenha desmarcado algum escopo)
        const filtradosPorScope = novos.filter((i) => {
          if (i.origem === 'meu') return scope.meus;
          if (i.origem === 'socio') return scope.socios;
          if (i.origem === 'parceiro') return scope.parceiros;
          return true;
        });

        setImoveis((prev) =>
          isLoadMore ? [...prev, ...filtradosPorScope] : filtradosPorScope
        );
        setUltimoDoc(nextPage);
        setTemMais(nextPage < (res.nbPages || 1) - 1);
      } catch (e) {
        console.error('❌ Algolia - erro ao carregar imóveis:', e);
      } finally {
        setLoading(false);
        setCarregandoMais(false);
      }
    },
    [
      usuario?.uid,
      groupsLoaded,
      buildAlgoliaParams,
      groups,
      scope.meus,
      scope.socios,
      scope.parceiros,
    ]
  );

  // primeira carga quando usuario+groups estiverem prontos
  useEffect(() => {
    if (usuario?.uid && groupsLoaded && !jaCarregouRef.current) {
      jaCarregouRef.current = true;
      fetchImoveis(false);
    }
  }, [usuario?.uid, groupsLoaded, fetchImoveis]);

  const handleCarregarMais = () => fetchImoveis(true);

  const handleBuscar = () => {
    setImoveis([]);
    setUltimoDoc(0);
    setTemMais(true);
    ultimoDocRef.current = 0;
    temMaisRef.current = true;
    fetchImoveis(false);
  };

  return (
    <div className="relative">
      <IconeComparador />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 px-6">
        <div className="lg:col-span-9 space-y-6">
          <h1 className="text-2xl font-bold">Explorar Imóveis</h1>

          {loading ? (
            <p>Carregando imóveis disponíveis...</p>
          ) : imoveis.length === 0 ? (
            <p>Nenhum imóvel encontrado.</p>
          ) : (
            <>
              <Listing
                imoveis={imoveis}
                onDelete={(id) =>
                  setImoveis((prev) => prev.filter((i) => i.id !== id))
                }
              />
              {temMais && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleCarregarMais}
                    className="px-4 py-2 bg-black text-white rounded"
                    disabled={carregandoMais}
                  >
                    {carregandoMais ? 'Carregando...' : 'Carregar mais imóveis'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="lg:col-span-3">
          <FiltroHome
            filtros={filtros}
            setFiltros={setFiltros}
            onBuscar={handleBuscar}
          />
        </div>
      </div>
    </div>
  );
}
